import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Legacy: Afghan Afghani formatter — kept for backward compatibility.
 * Locale-aware: reads the current UI language from localStorage
 * ('tasallot_language': dari | pashto | english | arabic, default dari)
 * and mirrors formatPrice's numeral-system rules:
 *   dari / pashto → fa-AF (۱۲۳), arabic → ar-AF (١٢٣), english → en-US (123)
 */
export function formatAFN(amount: number | string | null | undefined) {
  const num = Number(amount);
  if (isNaN(num)) return '؋ 0';

  let language = 'dari';
  try {
    const saved = localStorage.getItem('tasallot_language');
    if (saved) language = saved;
  } catch {
    // localStorage unavailable (SSR / privacy mode) — keep default
  }

  const locale =
    language === 'english' ? 'en-US' :
    language === 'arabic'  ? 'ar-AF' :
    'fa-AF'; // dari, pashto, and any unknown value

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'AFN',
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `AFN ${Math.round(num).toLocaleString('en-US')}`;
  }
}

/**
 * Resolve the Intl locale to use for a given currency + UI language.
 *
 * Rules:
 *  - english / uzbek  → Western-Arabic numerals (0-9)  via en-* locale
 *  - arabic           → Eastern-Arabic / Arabic-Indic numerals (١٢٣) via ar-* locale
 *  - dari / pashto    → Persian / Eastern-Arabic numerals (۱۲۳) via fa-AF locale
 *
 * The locale only controls the numeral system and punctuation; the currency
 * symbol is determined by the `currency` field in Intl.NumberFormat options.
 */
function resolveLocale(currency: string, language: string): string {
  const cur = currency.toUpperCase();

  switch (language) {
    case 'english':
      // Always Western numerals regardless of currency
      if (cur === 'OMR') return 'en-GB';   // en-GB formats OMR correctly
      if (cur === 'UZS') return 'en-US';
      if (cur === 'AFN') return 'en-US';
      return 'en-US';

    case 'uzbek':
      // Uzbek uses Latin script with Western numerals
      return 'uz-UZ';

    case 'arabic':
      // Arabic-Indic numerals (١٢٣)
      if (cur === 'OMR') return 'ar-OM';
      if (cur === 'AFN') return 'ar-AF';
      if (cur === 'UZS') return 'ar';
      if (cur === 'AED') return 'ar-AE';
      return 'ar';

    case 'pashto':
    case 'dari':
    default:
      // Persian/Dari numerals (۱۲۳)
      // fa-AF is well supported; fa-IR is the widely-deployed fallback
      if (cur === 'AFN') return 'fa-AF';
      return 'fa-IR';
  }
}

/**
 * Country-aware, language-aware price formatter.
 *
 * @param amount       — the numeric value to format
 * @param currencyCode — ISO 4217 code (AFN, OMR, UZS, AED, USD …)
 * @param language     — UI language key from AppContext
 *                       ('dari' | 'pashto' | 'english' | 'arabic' | 'uzbek')
 *                       Defaults to 'dari' (Persian numerals) when omitted.
 */
export function formatPrice(
  amount: number | string | null | undefined,
  currencyCode: string = 'AFN',
  language: string = 'dari',
): string {
  const num = Number(amount);
  if (isNaN(num)) return `${currencyCode} 0`;

  const currency = currencyCode.toUpperCase();
  const locale   = resolveLocale(currency, language);

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'OMR' ? 3 : 0,
    }).format(num);
  } catch {
    // Fallback: plain Western numerals
    return `${currency} ${Math.round(num).toLocaleString('en-US')}`;
  }
}

export function truncate(str: string, length: number) {
  if (!str) return '';
  return str.length > length ? `${str.substring(0, length)}...` : str;
}
